import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Formik } from "formik";
import * as Yup from "yup";
import { Grid, GridItem, Input, RadioGroup, Button, Checkbox, CheckboxGroup, TimePicker, DatePicker } from "@sainsburys-tech/fable";
import ButtonWithApiCall from "../ButtonWithApiCall";
import FormWrapper from "../FormWrapper";
import StoresDropdown from "../StoresDropdown";
import ZoneMultiSelector from "../ZoneMultiSelector";
import { ArrowDown } from '@sainsburys-tech/icons'
import {
  handleStartTimeOptions,
  handleEndTimeOptions
} from "../../utils/createTimeIntervalOptions.js";

import {
  REPEATS_DAILY,
  OCCURS_ONCE
} from "../../model/lighting-overrides/overrideFrequencyKeys";

moment.locale("en-GB");

const StoreOverrideForm = props => {

  const validationSchema = Yup.object().shape({
    overrideName: Yup.string()
      .required("Please enter name of override")
      .max(100, "Override name cannot exceed 100 characters"),
    storeId: Yup.string()
      .nullable()
      .required("Please select a store"),
    startDate: Yup.date()
      .typeError("Please enter a valid date")
      .required("Please enter a valid date")
      .test(
        "startDate must be before the endDate",
        "Start date must be before end date",
        function test(val) {
          if (!val || !this.parent.endDate || !this.parent.endTime) {
            return true;
          } else {
            return !moment(val).isAfter(moment(this.parent.endDate));
          }
        }
      ),
    endDate: Yup.date()
      .typeError("Please enter a valid date")
      .required("Please enter a valid date")
      .test(
        "endDate must be less than 14 days after startDate",
        "Override cannot repeat for more than 14 days",
        function test(val) {
          if (this.parent.occurOnceOrRepeatDaily === OCCURS_ONCE) {
            return true;
          }
          if (!val || !this.parent.startDate) {
            return true;
          }

          const startMoment = moment(this.parent.startDate);
          const endMoment = moment(val);

          return endMoment.diff(startMoment, "days") < 14;
        }
      ),
    scene: Yup.string().required("Please select a scene"),
    startTime: Yup.string()
      .required("Please enter a valid start time")
      .matches(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, "Please enter a valid time"),
    endTime: Yup.string()
      .required("Please enter a valid end time")
      .matches(
        /^([01]?[0-9]|2[0-3]):[0-5][0-9]( \(.*\))*$/,
        "Please enter a valid time"
      )
      .test(
        "startTime must not equal endTime",
        "Start time and end time cannot be the same",
        function test(val) {
          if (!this.parent.startDate || !this.parent.endDate) {
            return true;
          } else {
            return (
              !moment(this.parent.startDate).isSame(
                moment(this.parent.endDate),
                "day"
              ) || this.parent.startTime !== val
            );
          }
        }
      )
      .test(
        "endTime must be after startTime",
        "End time must be after start time",
        function test(val) {
          if (!this.parent.startDate || !this.parent.endDate) {
            return true;
          } else {
            return (
              !moment(this.parent.startDate).isSame(
                moment(this.parent.endDate),
                "day"
              ) ||
              !moment(this.parent.startTime, "h:mm").isAfter(
                moment(val, "h:mm")
              )
            );
          }
        }
      ),
    selectedZones: Yup.array().when("allZonesSelected", {
      is: val => !val,
      then: schema => schema
        .label("Zones")
        .of(Yup.object())
        .min(1, "Please select at least 1 zone")
    }),
    activeDays: Yup.array().test(
      "Must select at least 1 day",
      "Please select at least 1 day",
      function test(val) {
        if (this.parent.occurOnceOrRepeatDaily === OCCURS_ONCE) {
          return true;
        } else if (
          this.parent.occurOnceOrRepeatDaily === REPEATS_DAILY &&
          val.some(day => day.checked === true)
        ) {
          return true;
        } else {
          return false;
        }
      }
    )
  });

  const createEndTimeOptions = (startDate, startTime, endDate, occurs) => {
    if (!moment(startDate).isValid() || startTime === "") {
      return [];
    }
    const timeOptions = handleEndTimeOptions(
      startDate,
      startTime,
      endDate,
      occurs
    );

    return timeOptions;
  };

  const updateEndTimeField = (formProps, startDate, startTime, endDate) => {
    const endTimeOptions = createEndTimeOptions(
      startDate,
      startTime,
      endDate,
      formProps.values.occurOnceOrRepeatDaily
    );

    formProps.setFieldValue("endTimeOptions", endTimeOptions);
  };

  const createStartTimeOptions = () => {
    return handleStartTimeOptions();
  };

  const formatDateTime = (date, time) => {
    if (time === "" || time == null) {
      time = "00:00";
    }
    return moment(date).format("YYYY-MM-DD") + "T" + time + ":00";
  };

  const targetsInSameDropdown = (blurTarget, focusTarget) => {
    // Expected partial structure of an Autocomplete dropdown:

    /*
     * ...
     * <div class="form-input-group form-input-group--has-action">
     *   <div class="form-input-group__control form-autocomplete">
     *     <input role="textbox" id="downshift-0-input" name="downshift-0-input" type="text" class="form-autocomplete__input" />
     *   </div>
     *   <button class="form-button form-input-group__action" type="button" />
     * </div>
     * ...
     */

    // Focus can switch between the input and button elements, but this should not mark the dropdown field as touched (as this can trigger a validation error)

    if (blurTarget === null || focusTarget === null) {
      return false;
    }

    const focusFromInputToButton =
      blurTarget.tagName === "INPUT" &&
      focusTarget.tagName === "BUTTON" &&
      blurTarget.parentElement === focusTarget.previousElementSibling;

    const focusFromButtonToInput =
      blurTarget.tagName === "BUTTON" &&
      focusTarget.tagName === "INPUT" &&
      blurTarget.previousElementSibling === focusTarget.parentElement;

    return focusFromInputToButton || focusFromButtonToInput;
  };

  const mapStores = stores => {
    if (stores == null) return null;

    const storesList = stores.map(store => {
      return {
        isCommissioned: store.isCommissioned,
        storeId: store.storeId,
        name: store.name,
        otherName: store.otherName,
        region: store.region
      };
    });

    storesList.unshift(
      { storeId: "groups", name: "--- Groups ---" },
      { storeId: "priceSpikeStores", name: "Price Spike stores" },
      { storeId: "allStores", name: "All Stores" },
      { storeId: "stores", name: "--- Stores ---" }
    );

    return storesList;
  };

  const StoreField = field => {
    const formProps = field.formProps;
    return (
      <div
        onBlur={e => {
          const blurTarget = e.target;
          const focusTarget = e.relatedTarget;

          // Only touch field if focus is not moving between input and button within same dropdown
          if (!targetsInSameDropdown(blurTarget, focusTarget)) {
            formProps.setFieldTouched("storeId", true);
          }
        }}
      >
        <Grid cols={{ xs: 12, md: 12 }} gap="lg">
          <GridItem colSpan={{ xs: 12, md: 6 }}>
            <div style = {{marginTop: '15px'}}>
            <StoresDropdown
              name="storeId"
              handleStoreChanged={store => {
                let storeId = "";
                if (store) {
                  storeId = store.value;

                  if (store.value === "groups" || store.value === "stores") {
                    return;
                  }

                  if (
                    store.value === "priceSpikeStores" ||
                    store.value === "allStores"
                  ) {
                    const comissionedStores =
                      store.value === "allStores"
                        ? props.stores.filter(store => store.isCommissioned)
                        : props.stores.filter(
                            store => store.isCommissioned && store.isPriceSpike
                          );

                    props.handleRetrieveAllStoresZones(store);

                    formProps.setFieldValue("storeId", store.value);
                    formProps.setFieldValue(
                      "selectedStores",
                      comissionedStores
                    );

                    formProps.setFieldTouched("storeId", true);
                    return;
                  }

                  props.handleRetrieveZones(storeId, store);
                }
                formProps.setFieldValue("storeId", storeId);
                formProps.setFieldValue("selectedStores", []);
                formProps.setFieldTouched("storeId", true);

                if (storeId === "") {
                  formProps.setFieldValue("selectedZones", []);
                  formProps.setFieldTouched("selectedZones", false);
                  props.handleStoreDeselect(store);
                }
              }}
              stores={mapStores(props.stores)}
              selectedStore={props.selectedStore}
              storeId={formProps.values.storeId}
              error={formProps.touched.storeId && formProps.errors.storeId}
              label={"Add store"}
            />
            </div>
          </GridItem>
        </Grid>
      </div>
    );
  };

  const Scenes = field => {
    const formProps = field.formProps;
    return (
          <RadioGroup
            name="scene"
            style={{marginTop: '20px'}}
            isInline
            status={
              formProps.touched.scene && formProps.errors.scene
                ? { type: 'error', message: formProps.errors.scene }
                : undefined
            }
            options={props.scenes}
            value={formProps.values.scene}
            onChange={(value) => formProps.setFieldValue('scene', value)}
            label="Select a lighting scene"
          />
    );
  };

  const OverrideNameField = field => {
    const formProps = field.formProps;
    return (
      <Grid cols={{ xs: 12, md: 12 }} gap="lg">
        <GridItem colSpan={{ xs: 12, md: 3 }}>
          <Input
            name={"overrideName"}
            label="Name the override"
            status={
              formProps.touched.overrideName && formProps.errors.overrideName
                ? { type: 'error', message: formProps.errors.overrideName }
                : undefined
            }
            value={formProps.values.overrideName}
            onBlur={formProps.handleBlur}
            onChange={(value) => formProps.setFieldValue('overrideName', value)}
            placeholder={"e.g. Christmas trading"}
          />
        </GridItem>
      </Grid>
    );
  };
  const StartTime = field => {
    const formProps = field.formProps;
    return (
      <TimePicker
        name="startTime"
        label="Start time"
        value={formProps.values.startTime}
        onChange={(value) => {
          formProps.setFieldValue('startTime', value);
        }}
        onSelectTime={(value) => {
          const startDate = formProps.values.startDate;
          let startTimeVal = value;

          if (
            startTimeVal !== "" &&
            startTimeVal.split(":")[0].length === 1
          ) {
            startTimeVal = "0" + startTimeVal;
          }

          updateEndTimeField(
            formProps,
            startDate,
            startTimeVal,
            formProps.values.endDate
          );

          if (formProps.values.startDate) {
            const startDateTime = formatDateTime(
              formProps.values.startDate,
              startTimeVal
            );

            if (startTimeVal === "") {
              formProps.setFieldValue("endTime", "");
              formProps.setFieldTouched("endTime", false);
            } else {
              formProps.setFieldValue(
                "endTime",
                formProps.values.endTime.split(" ")[0]
              );
            }

            if (!moment(startDateTime).isValid()) {
              return;
            }

            formProps.setFieldValue("startDate", moment(startDateTime));
          }

          formProps.setFieldTouched("startTime", true);
        }}
        status={
          formProps.touched.startTime && formProps.errors.startTime
            ? { type: 'error', message: formProps.errors.startTime }
            : undefined
        }
        hasTrailingIcon={true}
      />
    );
  };

  const EndTime = field => {
    const formProps = field.formProps;
    return (
      <TimePicker
        name="endTime"
        label="End time"
        value={formProps.values.endTime}
        onChange={(value) => {
          formProps.setFieldValue('endTime', value);
        }}
        onSelectTime={(value) => {
          updateEndTimeField(
            formProps,
            formProps.values.startDate,
            formProps.values.startTime,
            formProps.values.endDate
          );

          let endTimeVal = value;

          if (endTimeVal !== "" && endTimeVal.split(":")[0].length === 1) {
            endTimeVal = "0" + endTimeVal;
          }

          formProps.setFieldValue("endTime", endTimeVal);
          formProps.setFieldTouched("endTime", true);

          if (
            formProps.values.startDate === null ||
            !formProps.values.startTime
          ) {
            return;
          }

          const startDateTime = formatDateTime(
            formProps.values.startDate,
            formProps.values.startTime
          );

          const time = endTimeVal.split(" ")[0];

          if (time === "") {
            return;
          }

          if (formProps.values.occurOnceOrRepeatDaily === REPEATS_DAILY) {
            return;
          }

          let endDateTime;
          if (!formProps.values.endDate) {
            formProps.setFieldValue(
              "endDate",
              moment(formProps.values.startDate)
            );

            endDateTime = formatDateTime(formProps.values.startDate, time);
          } else {
            endDateTime = formatDateTime(formProps.values.endDate, time);
          }

          if (
            !moment(startDateTime).isValid() ||
            !moment(endDateTime).isValid()
          ) {
            return;
          }

          if (
            moment(endDateTime).isSameOrBefore(moment(startDateTime)) &&
            moment(formProps.values.startDate).isSame(
              moment(endDateTime),
              "day"
            )
          ) {
            formProps.setFieldValue(
              "endDate",
              moment(endDateTime).add(1, "days")
            );

            updateEndTimeField(
              formProps,
              startDateTime,
              formProps.values.startTime,
              moment(endDateTime).add(1, "days")
            );
          } else {
            formProps.setFieldValue("endDate", moment(endDateTime));
          }
        }}
        status={
          formProps.touched.endTime && formProps.errors.endTime
            ? { type: 'error', message: formProps.errors.endTime }
            : undefined
        }
        hasTrailingIcon={true}
      />
    );
  };

  const StartAndEndDateTimes = field => {
    const formProps = field.formProps;
    const startDateStr = formProps.values.startDate ? moment(formProps.values.startDate).format('DD/MM/YYYY') : '';
    const endDateStr = formProps.values.endDate ? moment(formProps.values.endDate).format('DD/MM/YYYY') : '';

    return (
      <Grid cols={{ xs: 12, md: 12 }} gap="lg">
        <GridItem colSpan={{ xs: 12, md: 3 }}>
          <DatePicker
            name="startDate"
            label="Start date"
            placeholder="DD/MM/YYYY"
            value={startDateStr}
            shouldCloseOnSelect
            onChange={(value) => {
              const date = moment(value, 'DD/MM/YYYY');
              if (formProps.values.startTime && date.isValid()) {
                const startDatetime = formatDateTime(
                  date,
                  formProps.values.startTime
                );
                formProps.setFieldValue("startDate", moment(startDatetime));
              } else {
                formProps.setFieldValue("startDate", date);
              }

              if (date.isValid()) {
                updateEndTimeField(
                  formProps,
                  date,
                  formProps.values.startTime,
                  formProps.values.endDate
                );
              }
            }}
            status={
              formProps.touched.startDate && formProps.errors.startDate
                ? { type: 'error', message: formProps.errors.startDate }
                : undefined
            }
          />
        </GridItem>
        <GridItem colSpan={{ xs: 12, md: 2 }}>
          <StartTime formProps={formProps} />
        </GridItem>
        <GridItem colSpan={{ xs: 12, md: 1 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', height: '100%', paddingBottom: '0.5rem' }}>
            <strong>to</strong>
          </div>
        </GridItem>
        <GridItem colSpan={{ xs: 12, md: 3 }}>
          <DatePicker
            name="endDate"
            label="End date"
            placeholder="DD/MM/YYYY"
            value={endDateStr}
            shouldCloseOnSelect
            onChange={(value) => {
              const date = moment(value, 'DD/MM/YYYY');
              if (
                formProps.values.endTime.split(" ")[0] &&
                date.isValid()
              ) {
                const endDateTime = formatDateTime(
                  date,
                  formProps.values.endTime.split(" ")[0]
                );
                formProps.setFieldValue("endDate", moment(endDateTime));
              } else {
                formProps.setFieldValue("endDate", date);
              }

              if (date.isValid()) {
                updateEndTimeField(
                  formProps,
                  formProps.values.startDate,
                  formProps.values.startTime,
                  date
                );
              }
            }}
            status={
              formProps.touched.endDate && formProps.errors.endDate
                ? { type: 'error', message: formProps.errors.endDate }
                : undefined
            }
          />
        </GridItem>
        <GridItem colSpan={{ xs: 12, md: 2 }}>
          <EndTime formProps={formProps} />
        </GridItem>
      </Grid>
    );
  };

  const RepeatDaily = field => {
    const formProps = field.formProps;
    const startDateStr = formProps.values.startDate ? moment(formProps.values.startDate).format('DD/MM/YYYY') : '';
    const endDateStr = formProps.values.endDate ? moment(formProps.values.endDate).format('DD/MM/YYYY') : '';

    return (
      <React.Fragment>
        <Grid cols={{ xs: 12, md: 12 }} gap="lg">
          <GridItem colSpan={{ xs: 12, md: 3 }}>
            <DatePicker
              name="startDate"
              label="Start date"
              placeholder="DD/MM/YYYY"
              value={startDateStr}
              shouldCloseOnSelect
              onChange={(value) => {
                const date = moment(value, 'DD/MM/YYYY');
                if (formProps.values.startTime && date.isValid()) {
                  const startDatetime = formatDateTime(
                    date,
                    formProps.values.startTime
                  );
                  formProps.setFieldValue("startDate", moment(startDatetime));
                } else {
                  formProps.setFieldValue("startDate", date);
                }

                if (date.isValid()) {
                  updateEndTimeField(
                    formProps,
                    date,
                    formProps.values.startTime,
                    formProps.values.endDate
                  );
                }
              }}
              status={
                formProps.touched.startDate && formProps.errors.startDate
                  ? { type: 'error', message: formProps.errors.startDate }
                  : undefined
              }
            />
          </GridItem>
          <GridItem colSpan={{ xs: 12, md: 2 }}>
            <StartTime formProps={formProps} />
          </GridItem>
          <GridItem colSpan={{ xs: 12, md: 1 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', height: '100%', paddingBottom: '0.5rem' }}>
              <strong>to</strong>
            </div>
          </GridItem>
          <GridItem colSpan={{ xs: 12, md: 2 }}>
            <EndTime formProps={formProps} />
          </GridItem>
        </Grid>
        <CheckboxGroup
          label="Which days will this be active on?"
          name="activeDays"
          style={{marginTop:'20px'}}
          className="cb-c-store-override-form__active-days"
          isInline
          onChange={e => {
            const days = formProps.values.activeDays;
            const dayIndex = days.findIndex(d => d.name === e.target.value);
            days[dayIndex].checked = e.target.checked;

            formProps.setFieldValue("activeDays", days);
            formProps.setFieldTouched("activeDays", true);
          }}
          status={
            formProps.touched.activeDays && formProps.errors.activeDays
              ? { type: 'error', message: formProps.errors.activeDays }
              : undefined
          }
          options={formProps.values.activeDays.map(day => {
            return { value: day.name, label: day.name, isChecked: day.checked };
          })}
        />
        <Grid cols={{ xs: 12, md: 12 }} gap="lg">
          <GridItem colSpan={{ xs: 12, md: 3 }}>
            <DatePicker
              name="endDate"
              label="Occurs until"
              placeholder="DD/MM/YYYY"
              value={endDateStr}
              shouldCloseOnSelect
              onChange={(value) => {
                const date = moment(value, 'DD/MM/YYYY');
                const startDate = formProps.values.startDate;
                if (startDate && date.diff(startDate, "days") > 14) {
                  return;
                }
                const endTime = formProps.values.endTime.split(" ")[0];
                if (endTime && date.isValid()) {
                  const endDateTime = formatDateTime(date, endTime);
                  formProps.setFieldValue("endDate", moment(endDateTime));
                } else {
                  formProps.setFieldValue("endDate", date);
                }

                formProps.setFieldTouched("endTime", formProps.values.endTime !== "");
              }}
              status={
                formProps.touched.endDate && formProps.errors.endDate
                  ? { type: 'error', message: formProps.errors.endDate }
                  : undefined
              }
            />
          </GridItem>
        </Grid>
      </React.Fragment>
    );
  };

  const ZonesField = field => {
    const formProps = field.formProps;
    return (
      <React.Fragment>
        <div
          onBlur={e => {
            const blurTarget = e.target;
            const focusTarget = e.relatedTarget;

            // Only touch field if focus is not moving between input and button within same dropdown
            if (
              !targetsInSameDropdown(blurTarget, focusTarget) &&
              formProps.values.allZonesSelected
            ) {
              formProps.setFieldTouched("selectedZones", true);
            }
          }}
        >
          <Grid cols={{ xs: 12, md: 12 }} gap="lg">
            <GridItem colSpan={{ xs: 12, md: 6 }}>
              {formProps.values.allZonesSelected ? (
                <Input
                  style={{marginTop: '10px'}}
                  name="disabled-zone-multi-selector"
                  placeholder="Add zone(s)"
                  label="Add Zone(s)"
                  isDisabled
                  trailingButton={{
                    label: "Open",
                    icon: <ArrowDown />,
                    onClick: () => {},
                    isDisabled: true
                  }}
                />
              ) : (
                <ZoneMultiSelector
                  name="selectedZones"
                  zones={props.zones}
                  onSelect={selectedZones => {
                    formProps.setFieldValue("selectedZones", selectedZones);
                    formProps.setFieldTouched("selectedZones", true);
                  }}
                  defaultSelectedOptions={
                    props.selectedStore && props.zones
                      ? formProps.values.selectedZones
                      : []
                  }
                  error={
                    formProps.touched.selectedZones &&
                    formProps.errors.selectedZones
                  }
                  disabled={props.zonesDisabled}
                  label="Add Zone(s)"
                />
              )}
            </GridItem>
          </Grid>
        </div>
        <Checkbox
          name="allStoresCheckbox"
          label="Select all zones"
          error={
            formProps.touched.allZonesSelected &&
            formProps.errors.allZonesSelected
          }
          defaultChecked={formProps.values.allZonesSelected}
          value={formProps.values.allZonesSelected ? "true" : "false"}
          onBlur={formProps.handleBlur}
          onChange={e => {
            formProps.setFieldValue("allZonesSelected", e.target.checked);
            formProps.setFieldTouched("selectedZones", false);

            if (e.target.checked) {
              formProps.setFieldValue("selectedZones", []);
            }
          }}
          isDisabled={props.zonesDisabled}
        />
      </React.Fragment>
    );
  };

  const SaveAndCancelButtons = field => {
    const formProps = field.formProps;
    return (
      <Grid cols={{ xs: 12, md: 12 }} gap="lg">
        <GridItem colSpan={{ xs: 12, md: 3 }}>
            <div>
            <ButtonWithApiCall
              type="submit"
              onClick={() => props.onSubmit(formProps.values)}
              onComplete={override => props.onSaveComplete(override)}
              fullWidth
              disabled={!formProps.isValid}
            />
            {props.errorSubmitting && (
              <span style={{ color: '#d32f2f', fontSize: '0.875rem', marginTop: '0.5rem', display: 'block' }}>There was an error submitting</span>
            )}
          </div>
        </GridItem>
        <GridItem colSpan={{ xs: 12, md: 4 }}>
          <Button onClick={props.onCancel} variant="secondary" isFullWidth>
            Cancel
          </Button>
        </GridItem>
      </Grid>
    );
  };

  return (
    <Formik
      enableReinitialize
      initialValues={props.initialValues}
      isInitialValid={() => {
        return validationSchema.isValidSync(props.initialValues);
      }}
      touched={{}}
      validateOnChange={true}
      validationSchema={validationSchema}
      render={formProps => (
        <FormWrapper isDirty={formProps.dirty}>
          <section>
            <section>
              {" "}
              <OverrideNameField formProps={formProps} />
            </section>
            <section>
              <h6 style={{marginTop:'20px'}}>When do you want the override to happen?</h6>
              {props.initialValues.overrideId === undefined && (
                <React.Fragment>
                  <Grid cols={{ xs: 12, md: 12 }} gap="lg">
                    <GridItem colSpan={{ xs: 12, md: 8 }}>
                      <p>
                        Set an override so it occurs once, for example overnight
                        or to repeat, for example if it’s for the same time over
                        multiple days.
                      </p>
                    </GridItem>
                  </Grid>
                  <RadioGroup
                    label="When do you want the override to happen?"
                    hasHiddenLabel
                    isInline
                    name="occurOnceOrRepeatDaily"
                    value={formProps.values.occurOnceOrRepeatDaily}
                    onChange={(value) => formProps.setFieldValue('occurOnceOrRepeatDaily', value)}
                    options={[
                      {
                        value: OCCURS_ONCE,
                        label: "Occurs once"
                      },
                      { value: REPEATS_DAILY, label: "Repeats daily" }
                    ]}
                  />
                </React.Fragment>
              )}

              {formProps.values.occurOnceOrRepeatDaily === OCCURS_ONCE && (
                <StartAndEndDateTimes formProps={formProps} />
              )}
              {formProps.values.occurOnceOrRepeatDaily === REPEATS_DAILY && (
                <RepeatDaily formProps={formProps} />
              )}
            </section>
            <section>
              <StoreField formProps={formProps} />
            </section>
            <section className="fable:mb-0">
              <Scenes formProps={formProps} />
            </section>

            <section className="fable:mb-8">
              <ZonesField formProps={formProps} />
            </section>
            <section>
              <SaveAndCancelButtons formProps={formProps} />
            </section>
          </section>
        </FormWrapper>
      )}
    />
  );
};

StoreOverrideForm.propTypes = {
  initialValues: PropTypes.shape({
    overrideName: PropTypes.string,
    storeId: PropTypes.string,
    scene: PropTypes.string,
    zones: PropTypes.array,
    allZonesSelected: PropTypes.bool,
    startTime: PropTypes.string,
    startDate: PropTypes.object,
    endTime: PropTypes.string,
    endDate: PropTypes.object,
    endTimeOptions: PropTypes.array
  }),
  zonesDisabled: PropTypes.bool
};

export default StoreOverrideForm;
